import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { CommonModule } from '@angular/common'
import { UserModel } from '../../../../core/models/user.model'
import { MultiSelectModule } from 'primeng/multiselect'
import { DatePickerModule } from 'primeng/datepicker' // Changed from CalendarModule
import { CardModule } from 'primeng/card'
import { RadioButtonModule } from 'primeng/radiobutton'
import { DropdownModule } from 'primeng/dropdown'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { forkJoin, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { UserService } from '../../../users/services/user.service'
import { AuthService } from '../../../../core/services/auth.service'
import { ButtonModule } from 'primeng/button'
import { TimeSlotTools } from '../../../../core/tools/time-slot-tools'
import { SelectModule } from 'primeng/select'

@Component({
  selector: 'app-affair-schedule-intervention-schedule',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MultiSelectModule,
    DatePickerModule, // Changed from CalendarModule
    CardModule,
    RadioButtonModule,
    DropdownModule,
    ToggleSwitchModule,
    ButtonModule,
    SelectModule,
  ],
  templateUrl: './affair-schedule-intervention-schedule.component.html',
  styleUrls: ['./affair-schedule-intervention-schedule.component.scss'],
  // Ajouter la classe CSS spécifique pour pouvoir cibler ce composant
  host: {
    class: 'affair-schedule-intervention-schedule',
  },
})
export class AffairScheduleInterventionScheduleComponent
  implements OnInit, OnChanges
{
  /**
   * Mémorise le dernier tableau d'IDs de techniciens utilisé pour la vérification ERP
   */
  private lastCheckedTechnicianIds: number[] = []
  @Input() droppedDetails: any
  @Input() selectedTechnicians: UserModel[] = []
  @Input() dateRange: Date[] = []
  @Input() scheduleForm: FormGroup // Nouveau input pour recevoir le formulaire du parent
  @Input() usersList: any[] = [] // New input to receive preloaded users from parent

  @Output() formStateChanged = new EventEmitter<{ valid: boolean; data: any }>()
  @Output() formScheduleChanged = new EventEmitter<any>()

  @Output() techniciansIncompatibleErpChanged = new EventEmitter<boolean>()

  public form: FormGroup
  public users: any[] = []
  public formInitialized = false
  public currentUser: any = null

  // Propriétés pour les créneaux horaires
  public selectedTimeSlot: 'morning' | 'afternoon' | 'custom' = 'custom'
  public timeSlotMismatch: boolean = false

  // Add property for minimum date
  public minDate: Date = new Date()

  private destroyed$ = new Subject<void>()
  private destroyRef = inject(DestroyRef)
  private authService = inject(AuthService)

  constructor(
    private fb: FormBuilder,
    private usersService: UserService
  ) {}

  ngOnInit() {
    // Register destroy hook
    this.destroyRef.onDestroy(() => {
      this.destroyed$.next()
      this.destroyed$.complete()
    })

    // Utiliser le formulaire du parent s'il est fourni, sinon initialiser localement
    if (this.scheduleForm) {
      this.form = this.scheduleForm
      this.formInitialized = true

      // S'assurer que les validateurs sont correctement configurés
      this.updateFormValidators()

      // Observer les changements sur startDate et les créneaux horaires
      this.form.valueChanges.subscribe(() => {
        // Vérifier la cohérence des créneaux horaires
        if (this.form.get('isTimeSlot')?.value) {
          this.timeSlotMismatch = TimeSlotTools.checkTimeSlotConsistency(
            this.form,
            this.selectedTimeSlot
          )
        }
        this.emitFormData()
      })

      // Émettre les données initiales
      setTimeout(() => this.emitFormData(), 0)
    } else {
      this.initForm()
    }

    // Use preloaded users if available
    if (this.usersList && this.usersList.length > 0) {
      this.users = this.usersList

      if (this.formInitialized) {
        this.setupSelectedTechnicians()
      }

      if (this.currentUser) {
        this.updateAssistantUser()
      }
    } else {
      // Fallback: load users if not provided by parent
      this.loadUsers()
    }

    // Récupérer l'utilisateur connecté
    this.authService
      .getCurrentUser()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.currentUser = user
        if (this.currentUser && this.form) {
          this.updateAssistantUser()
        }
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    // Vérifier si le formulaire parent est nouvellement fourni
    if (changes['scheduleForm'] && changes['scheduleForm'].currentValue) {
      const newForm = changes['scheduleForm'].currentValue

      if (!this.form || this.form !== newForm) {
        this.form = newForm
        this.formInitialized = true

        // Configurer les validateurs et les écouteurs
        this.updateFormValidators()

        // Si nous avons déjà des données, mettons-les à jour dans le formulaire
        if (this.droppedDetails?.date) {
          this.form.patchValue({ startDate: this.droppedDetails.date })
        }

        if (this.selectedTechnicians?.length > 0) {
          this.setupSelectedTechnicians()
        }

        if (this.currentUser) {
          this.updateAssistantUser()
        }

        // Émettre les données du formulaire
        this.emitFormData()
      }
    }

    if (changes['droppedDetails'] && changes['droppedDetails'].currentValue) {
      if (!this.formInitialized) {
        this.initForm()
      } else {
        if (this.droppedDetails.date) {
          // Vérifier si la date est valide
          const dateObj = this.ensureValidDate(this.droppedDetails.date)

          // Mise à jour du formulaire avec les nouvelles données
          this.form.get('startDate')?.setValue(dateObj)
        }

        // Mettre à jour la durée en fonction des produits sélectionnés
        this.updateDurationFromProducts()
      }
    }

    if (
      changes['selectedTechnicians'] &&
      changes['selectedTechnicians'].currentValue &&
      this.formInitialized
    ) {
      this.setupSelectedTechnicians()
    }

    if (
      changes['dateRange'] &&
      changes['dateRange'].currentValue &&
      this.form
    ) {
      this.form.patchValue({
        dateRange: this.dateRange,
      })
    }

    // Check if usersList has been updated from parent
    if (changes['usersList'] && changes['usersList'].currentValue) {
      this.users = changes['usersList'].currentValue

      if (this.formInitialized) {
        this.setupSelectedTechnicians()
      }

      if (this.currentUser) {
        this.updateAssistantUser()
      }
    }
  }

  setupSelectedTechnicians() {
    if (
      !this.form ||
      !this.selectedTechnicians ||
      this.selectedTechnicians.length === 0
    )
      return

    const technicianIds = this.selectedTechnicians.map((tech) => tech.id)
    this.form.get('techniciansIds')?.setValue(technicianIds)

    // Check ERP source compatibility when technicians are initially set up
    this.checkTechniciansErpSource(technicianIds)
  }

  initForm() {
    // Cette méthode n'est utilisée que si aucun formulaire n'est fourni par le parent
    let eventDate = new Date() // Date par défaut

    // Traiter la date de l'événement si disponible
    if (this.droppedDetails?.date) {
      eventDate = this.ensureValidDate(this.droppedDetails.date)
    }

    // Calculer une durée par défaut basée sur les données du produit
    let defaultDuration = null

    // Vérifier si nous avons des produits groupés
    if (
      this.droppedDetails?.productsData &&
      Array.isArray(this.droppedDetails.productsData)
    ) {
      // Calculer la durée totale pour tous les produits
      let totalDurationMinutes = 0

      // Si nous avons une durée totale précalculée, l'utiliser directement
      if (this.droppedDetails.duration) {
        totalDurationMinutes = Number(this.droppedDetails.duration)
      }
      // Sinon, calculer la somme des durées des produits
      else {
        totalDurationMinutes = this.droppedDetails.productsData.reduce(
          (sum: any, productData: any) =>
            sum + (productData?.product?.duration || 0),
          0
        )
      }

      // Convertir en objet Date pour le champ duration
      const hours = Math.floor(totalDurationMinutes / 60)
      const minutes = totalDurationMinutes % 60

      const durationDate = new Date()
      durationDate.setHours(hours)
      durationDate.setMinutes(minutes)
      durationDate.setSeconds(0)

      defaultDuration = durationDate
    }
    // Cas d'un seul produit (code existant)
    else if (this.droppedDetails?.productData?.product?.duration) {
      const durationMinutes = this.droppedDetails.productData.product.duration
      const hours = Math.floor(durationMinutes / 60)
      const minutes = durationMinutes % 60

      const durationDate = new Date()
      durationDate.setHours(hours)
      durationDate.setMinutes(minutes)
      durationDate.setSeconds(0)

      defaultDuration = durationDate
    }

    const techniciansIds = this.selectedTechnicians.map((tech) => tech.id)

    this.form = this.fb.group({
      startDate: [eventDate, Validators.required],
      duration: [defaultDuration],
      isTimeSlot: [false],
      startTimeSlot: [null],
      endTimeSlot: [null],
      techniciansIds: [[...techniciansIds]],
      userAssistantId: [null], // Initialisé à null, sera mis à jour quand l'utilisateur sera chargé
      isConfirmed: [true],
      dateRange: [this.dateRange],
    })

    this.formInitialized = true

    // Si l'utilisateur est déjà chargé, mettons à jour le champ assistant
    if (this.currentUser) {
      this.updateAssistantUser()
    }

    // Émettre des mises à jour chaque fois que le formulaire change
    this.form.valueChanges.subscribe(() => {
      if (this.form.get('isTimeSlot')?.value) {
        this.timeSlotMismatch = TimeSlotTools.checkTimeSlotConsistency(
          this.form,
          this.selectedTimeSlot
        )
      }
      this.emitFormData()
    })

    // Émettre les données initiales
    setTimeout(() => this.emitFormData(), 0)
  }

  emitFormData() {
    const formData = this.form.value

    this.formStateChanged.emit({
      valid: this.form.valid,
      data: formData,
    })

    this.formScheduleChanged.emit(formData)
  }

  // Nouvelle méthode pour mettre à jour l'utilisateur assistant
  updateAssistantUser() {
    if (this.currentUser && this.form) {
      const userId = Number(this.currentUser.id)

      // Verify the user exists in the dropdown options
      const userExists = this.users.some((u) => u.value === userId)

      if (userExists) {
        this.form.get('userAssistantId')?.setValue(userId)
      } else {
        // User not found in options list
      }

      this.emitFormData()
    }
  }

  // Vérifie si le formulaire est valide
  public isFormValid(): boolean {
    return this.form?.valid || false
  }

  // Obtenir les données du formulaire
  public getFormData(): any {
    return this.form?.value
  }

  // Méthode pour basculer l'option de créneau horaire
  toggleTimeSlot(): void {
    const isTimeSlot = this.form.get('isTimeSlot')?.value

    if (isTimeSlot) {
      this.form.get('startTimeSlot')?.setValidators([Validators.required])
      this.form.get('endTimeSlot')?.setValidators([Validators.required])
      this.form.get('duration')?.clearValidators()

      // Définir le créneau matin par défaut quand on active l'option
      this.selectTimeSlot('morning')
    } else {
      this.form.get('startTimeSlot')?.clearValidators()
      this.form.get('endTimeSlot')?.clearValidators()
      this.form.get('duration')?.setValidators([Validators.required])
      this.selectedTimeSlot = 'custom'
    }

    this.form.get('startTimeSlot')?.updateValueAndValidity({ emitEvent: false })
    this.form.get('endTimeSlot')?.updateValueAndValidity({ emitEvent: false })
    this.form.get('duration')?.updateValueAndValidity({ emitEvent: false })
  }

  // Méthode pour sélectionner un créneau horaire
  selectTimeSlot(slot: 'morning' | 'afternoon' | 'custom'): void {
    const startTimeSlotControl = this.form.get('startTimeSlot')
    const endTimeSlotControl = this.form.get('endTimeSlot')

    if (!startTimeSlotControl || !endTimeSlotControl) return

    this.selectedTimeSlot = slot

    switch (slot) {
      case 'morning': {
        const startTime = new Date()
        startTime.setHours(8, 0, 0, 0)
        startTimeSlotControl.setValue(startTime)

        const endTime = new Date()
        endTime.setHours(12, 0, 0, 0)
        endTimeSlotControl.setValue(endTime)
        break
      }
      case 'afternoon': {
        const startTime = new Date()
        startTime.setHours(13, 0, 0, 0)
        startTimeSlotControl.setValue(startTime)

        const endTime = new Date()
        endTime.setHours(18, 0, 0, 0)
        endTimeSlotControl.setValue(endTime)
        break
      }
      case 'custom':
      default:
        startTimeSlotControl.setValue(null)
        endTimeSlotControl.setValue(null)
        break
    }

    // Vérifier la cohérence après changement de créneau
    this.timeSlotMismatch = TimeSlotTools.checkTimeSlotConsistency(
      this.form,
      this.selectedTimeSlot
    )
  }

  // Mise à jour du créneau pour correspondre à la date actuelle
  updateTimeSlotToMatchDate(): void {
    const startDate = this.form.get('startDate')?.value
    if (!startDate || !(startDate instanceof Date)) {
      return
    }

    const currentHour = startDate.getHours()

    // Determine appropriate time slot based on startDate hour
    if (currentHour >= 8 && currentHour < 12) {
      // Morning: 8h-12h
      this.selectTimeSlot('morning')
    } else if (currentHour >= 13 && currentHour < 18) {
      // Afternoon: 13h-18h
      this.selectTimeSlot('afternoon')
    } else {
      // If the hour doesn't fit any predefined slot, keep current slot
      // or default to morning if no valid slot is selected
      if (
        this.selectedTimeSlot !== 'morning' &&
        this.selectedTimeSlot !== 'afternoon'
      ) {
        this.selectTimeSlot('morning')
      } else {
        // Reapply the current slot to refresh the values
        this.selectTimeSlot(this.selectedTimeSlot)
      }
    }

    // Reset mismatch flag as we've now adjusted the slot
    this.timeSlotMismatch = false
  }

  // Nouvelle méthode pour restaurer les données du formulaire
  public restoreFormData(data: any): void {
    if (!data || !this.form) return

    // Create a copy of the data to avoid modifying the original
    const formData = { ...data }

    // Conversion des champs de date en objets Date si nécessaire
    if (formData.startDate) {
      try {
        // Check if it's a string (from JSON)
        if (typeof formData.startDate === 'string') {
          formData.startDate = new Date(formData.startDate)
        }
        // Check if it's a timestamp or date object that needs to be cloned
        else if (
          formData.startDate instanceof Date ||
          typeof formData.startDate === 'number'
        ) {
          formData.startDate = new Date(formData.startDate)
        }

        // Validate the date
        if (isNaN(formData.startDate.getTime())) {
          formData.startDate = new Date() // Fallback to current date
        }
      } catch (e) {
        formData.startDate = new Date() // Fallback to current date
      }
    }

    // Use the same robust conversion for other date fields
    ;['startTimeSlot', 'endTimeSlot', 'duration'].forEach((field) => {
      if (formData[field]) {
        try {
          if (typeof formData[field] === 'string') {
            formData[field] = new Date(formData[field])
          } else if (
            formData[field] instanceof Date ||
            typeof formData[field] === 'number'
          ) {
            formData[field] = new Date(formData[field])
          }

          if (isNaN(formData[field].getTime())) {
            formData[field] = null
          }
        } catch (e) {
          formData[field] = null
        }
      }
    })

    // Apply form values in two steps to ensure startDate is applied first
    if (formData.startDate) {
      this.form.get('startDate')?.setValue(formData.startDate)
    }

    // Then apply the rest of the values
    this.form.patchValue(formData)

    // Déterminer le type de créneau horaire s'il y a des valeurs
    if (formData.isTimeSlot && formData.startTimeSlot && formData.endTimeSlot) {
      this.selectedTimeSlot = TimeSlotTools.determineTimeSlotType(
        formData.startTimeSlot,
        formData.endTimeSlot
      )
    }

    // S'assurer que les validateurs sont correctement mis à jour
    this.updateFormValidators()

    // Attendre un cycle avant d'émettre les données pour s'assurer que les modifications sont reflétées
    setTimeout(() => {
      this.emitFormData()
    }, 0)
  }

  // Nouvelle méthode pour mettre à jour la durée en fonction des produits
  updateDurationFromProducts() {
    if (!this.form || !this.droppedDetails) return

    let totalDurationMinutes = 0

    // Vérifier si nous avons une durée totale précalculée
    if (this.droppedDetails.duration) {
      totalDurationMinutes = Number(this.droppedDetails.duration)
    }
    // Vérifier si nous avons des produits groupés
    else if (
      this.droppedDetails.productsData &&
      Array.isArray(this.droppedDetails.productsData)
    ) {
      totalDurationMinutes = this.droppedDetails.productsData.reduce(
        (sum: any, productData: any) =>
          sum + (productData?.product?.duration || 0),
        0
      )
    }
    // Cas d'un seul produit
    else if (this.droppedDetails?.productData?.product?.duration) {
      totalDurationMinutes = this.droppedDetails.productData.product.duration
    }

    if (totalDurationMinutes > 0) {
      const hours = Math.floor(totalDurationMinutes / 60)
      const minutes = totalDurationMinutes % 60

      const durationDate = new Date()
      durationDate.setHours(hours)
      durationDate.setMinutes(minutes)
      durationDate.setSeconds(0)

      this.form.get('duration')?.setValue(durationDate)
    }
  }

  // Check for time slot consistency
  private checkTimeSlotConsistency(): boolean {
    const startDate = this.form.get('startDate')?.value
    const startTimeSlot = this.form.get('startTimeSlot')?.value
    const endTimeSlot = this.form.get('endTimeSlot')?.value

    if (!startDate || !startTimeSlot || !endTimeSlot) {
      return false
    }

    // Extract hours
    let startDateHour: number = 0
    let startSlotHour: number = 0
    let endSlotHour: number = 0

    if (startDate instanceof Date) {
      startDateHour = startDate.getHours()
    }

    if (startTimeSlot instanceof Date) {
      startSlotHour = startTimeSlot.getHours()
    } else if (typeof startTimeSlot === 'string') {
      startSlotHour = parseInt(startTimeSlot.split(':')[0], 10)
    }

    if (endTimeSlot instanceof Date) {
      endSlotHour = endTimeSlot.getHours()
    } else if (typeof endTimeSlot === 'string') {
      endSlotHour = parseInt(endTimeSlot.split(':')[0], 10)
    }

    // Check if startDate hour is within the time slot
    if (startDateHour < startSlotHour || startDateHour >= endSlotHour) {
      return true
    }

    // If a specific time slot is selected, verify its consistency
    if (this.selectedTimeSlot !== 'custom') {
      const isValidMorning =
        this.selectedTimeSlot === 'morning' &&
        startSlotHour === 8 &&
        endSlotHour === 12

      const isValidAfternoon =
        this.selectedTimeSlot === 'afternoon' &&
        startSlotHour === 13 &&
        endSlotHour === 18

      if (!isValidMorning && !isValidAfternoon) {
        return true
      }
    }

    return false
  }

  // Nouvelle méthode pour mettre à jour les validateurs en fonction de isTimeSlot
  private updateFormValidators() {
    const isTimeSlot = this.form.get('isTimeSlot')?.value

    if (isTimeSlot) {
      this.form.get('startTimeSlot')?.setValidators([Validators.required])
      this.form.get('endTimeSlot')?.setValidators([Validators.required])
      this.form.get('duration')?.clearValidators()
    } else {
      this.form.get('startTimeSlot')?.clearValidators()
      this.form.get('endTimeSlot')?.clearValidators()
      this.form.get('duration')?.setValidators([Validators.required])
    }

    this.form.get('startTimeSlot')?.updateValueAndValidity({ emitEvent: false })
    this.form.get('endTimeSlot')?.updateValueAndValidity({ emitEvent: false })
    this.form.get('duration')?.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * S'assure que la date est bien un objet Date valide
   */
  private ensureValidDate(dateInput: any): Date {
    if (!dateInput) return new Date()

    try {
      // Si c'est déjà une Date, la retourner directement
      if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        return dateInput
      }

      // Sinon, essayer de créer une nouvelle Date
      const date = new Date(dateInput)

      // Vérifier si la date est valide
      if (!isNaN(date.getTime())) {
        return date
      }
    } catch (e) {
      // Error converting to Date
    }

    // En cas d'échec, retourner la date actuelle
    return new Date()
  }

  // Private method to load users as fallback
  private loadUsers() {
    this.usersService.getForSelect().subscribe((users) => {
      this.users = users

      if (this.formInitialized) {
        this.setupSelectedTechnicians()
      }

      if (this.currentUser) {
        this.updateAssistantUser()
      }
    })
  }

  selectedTechniciansInfo: UserModel[] = []

  checkTechniciansErpSource(technicianIds: number[]): void {
    // Vérifie si les IDs sont identiques à la dernière vérification
    const idsString = JSON.stringify(technicianIds || [])
    const lastIdsString = JSON.stringify(this.lastCheckedTechnicianIds || [])
    if (idsString === lastIdsString) {
      // Ne relance pas la vérification si les IDs n'ont pas changé
      return
    }

    this.lastCheckedTechnicianIds = technicianIds ? [...technicianIds] : []

    if (!technicianIds || technicianIds.length === 0) {
      this.selectedTechniciansInfo = []
      this.techniciansIncompatibleErp = false
      this.techniciansIncompatibleErpChanged.emit(false)
      return
    }

    const requests = technicianIds.map((id) => this.usersService.getUser(id))

    forkJoin(requests).subscribe({
      next: (users: UserModel[]) => {
        this.selectedTechniciansInfo = users
        this.setTechniciansIncompatibleErp()
      },
      error: (err) => {
        console.error('Erreur lors du chargement des techniciens :', err)
        this.techniciansIncompatibleErp = false
        this.techniciansIncompatibleErpChanged.emit(false)
      },
    })
  }

  techniciansIncompatibleErp = false

  setTechniciansIncompatibleErp(): void {
    if (this.selectedTechniciansInfo.length < 2) {
      this.techniciansIncompatibleErp = false
      this.techniciansIncompatibleErpChanged.emit(false)
      return
    }

    const firstSource = this.selectedTechniciansInfo[0]?.erpSource

    const isIncompatible = !this.selectedTechniciansInfo.every(
      (tech) => tech.erpSource === firstSource
    )

    this.techniciansIncompatibleErp = isIncompatible
    this.techniciansIncompatibleErpChanged.emit(isIncompatible)
  }
}
